import React from 'react';
import { Badge, Button, Modal, Paragraph, Spinner } from 'elmo-elements';
import { useParams } from 'react-router-dom';
import { ImageOutlineIcon } from '@/components/Icons';
import {
  useGetElearningBehaviourSettingsQuery,
  useGetElearningQuery,
  useGetFramesQuery,
  useGetResourcesQuery,
} from '@/modules/services';
import { FrameDataType } from '@/modules/types/frame';
import { ElearningParamType } from '@/pages/Module/routes';
import PlayerUi from '@/components/Player';
import './KeepAsImageModal.scss';

interface KeepAsImageModalProps {
  isOpen: boolean;
  isLoading: boolean;
  isGuardrailBlocked?: boolean;
  currentFrame?: FrameDataType;
  onClose: () => void;
  onConfirm: () => void;
}

const KeepAsImageModal = ({
  isOpen,
  isLoading,
  isGuardrailBlocked = false,
  currentFrame,
  onClose,
  onConfirm,
}: KeepAsImageModalProps): React.ReactElement | null => {
  const { elearningId, frameId } = useParams<ElearningParamType>();
  const elearning = useGetElearningQuery(elearningId);
  const frames = useGetFramesQuery({ elearningId });
  const resources = useGetResourcesQuery({ elearningId });
  const { data: elearningBehaviourSettingsData } =
    useGetElearningBehaviourSettingsQuery();

  if (!isOpen) return null;

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  // Build screenshot URL from frame metadata
  const tenantId = elearning.data?.tenantId;
  const slideIndex = currentFrame?.frameMetadata?.slideIndex;
  const pptElearningId = currentFrame?.frameMetadata?.pptElearningId;

  const screenshotUrl =
    tenantId != null && slideIndex != null && pptElearningId
      ? `/content/${tenantId}/elearnings/${pptElearningId}/slide-screenshots/${slideIndex}.png`
      : undefined;

  const placeholderCanvas = (
    <div
      className="keep-as-image-modal__canvas"
      style={
        screenshotUrl ? { backgroundImage: `url(${screenshotUrl})` } : undefined
      }
    >
      <div className="keep-as-image-modal__placeholder">
        <ImageOutlineIcon />
        <span>Original PowerPoint slide — inserted here</span>
      </div>
    </div>
  );

  return (
    <Modal
      className="keep-as-image-modal"
      type="large"
      isOpened={isOpen}
      closeModal={handleClose}
      title="Replace with image"
      hasCloseFooterButton={true}
      primaryButton={
        <div className="keep-as-image-modal__actions">
          <Button
            type="default"
            isUppercase={false}
            onClick={handleClose}
            isDisabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            isUppercase={false}
            onClick={onConfirm}
            isDisabled={isLoading}
            icon={
              isLoading ? <Spinner size="s" /> : <ImageOutlineIcon size="sm" />
            }
          >
            Replace with image
          </Button>
        </div>
      }
      closeLabel="Close"
    >
      <Paragraph color="gray">
        {isGuardrailBlocked
          ? 'Replaces with the original PowerPoint screenshot. The image slide is not editable.'
          : 'Adds a copy of this slide using the original PowerPoint screenshot. The new image slide is not editable.'}
      </Paragraph>

      <div className="keep-as-image-modal__preview-wrapper">
        <div className="keep-as-image-modal__preview">
          {elearning.data && (
            <PlayerUi
              isPlayable={false}
              currentFrameId={frameId || ''}
              isQuizEnabled={false}
              elearning={elearning.data}
              frames={frames.data || []}
              resources={resources.data || []}
              helperContent={[]}
              helperFaq={[]}
              elearningBehaviourSettings={elearningBehaviourSettingsData}
              canvas={placeholderCanvas}
            />
          )}
        </div>
      </div>

      <div className="keep-as-image-modal__meta">
        <Badge
          type="warning"
          role="status"
          className="keep-as-image-modal__badge"
          icon={<span className="keep-as-image-modal__badge-dot" />}
        >
          Image only &middot; not editable
        </Badge>
      </div>
    </Modal>
  );
};

export default KeepAsImageModal;
